import {
  CalendarIcon,
  CopyIcon,
  MapPinIcon,
  RefreshCwIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { useEntitlements } from '@/hooks/use-entitlements';

import { assignmentState, formatDate, formatTimeRange, isRecurring } from './helpers';
import type { Job } from './types';

interface Props {
  job: Job;
  onSelect: (jobId: string) => void;
  onCopy: (job: Job) => void;
}

export function JobCard({ job, onSelect, onCopy }: Props) {
  const { t } = useLanguage();
  const { has } = useEntitlements();

  const recurring = isRecurring(job);
  const assignment = has('employees') ? assignmentState(job) : 'unknown';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(job.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(job.id);
      }}
      className="bg-card border rounded-xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow cursor-pointer"
      data-testid={`job-card-${job.id}`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm truncate">{job.title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            <UserIcon className="w-3 h-3 shrink-0" />
            {job.customer_name}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(job);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label={t.jobCopyAction}
            title={t.jobCopyAction}
            data-testid={`job-copy-${job.id}`}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1 -m-1"
          >
            <CopyIcon className="w-3.5 h-3.5" />
          </button>
          {recurring && (
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 bg-primary/10 text-primary"
              data-testid={`job-visit-count-${job.id}`}
            >
              <RefreshCwIcon className="w-3 h-3" />
              {job.visit_count} {t.jobBadgeVisits}
            </span>
          )}
          {assignment === 'unassigned' && (
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
              data-testid={`job-assignee-badge-${job.id}`}
            >
              <UserPlusIcon className="w-3 h-3" />
              {t.jobBadgeUnassigned}
            </span>
          )}
          {assignment === 'assigned' && (
            <span
              className="text-[11px] text-muted-foreground flex items-center gap-1"
              data-testid={`job-assignee-badge-${job.id}`}
            >
              <UsersIcon className="w-3 h-3" />
              {job.assigned_employee_count} {t.jobBadgeAssigned}
            </span>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
          <span>
            {formatDate(job.start_date)} · {formatTimeRange(job)}
          </span>
        </div>
        {job.service_address && (
          <div className="flex items-start gap-1.5">
            <MapPinIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span className="truncate">{job.service_address}</span>
          </div>
        )}
        {job.description && (
          <p className="mt-0.5 italic text-[11px] bg-muted/60 rounded px-2 py-1">
            {job.description}
          </p>
        )}
      </div>
    </div>
  );
}
